import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { IDirectEntity, IDirectEntityCriteria } from '../direct-entity.interface';
import * as _ from 'underscore';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DataService } from 'src/app/shared/services/data.service';
import { Api } from 'src/app/shared/constants/api';
import { NGXLogger } from 'ngx-logger';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Message } from 'src/app/shared/constants/message';

@Component({
    selector: 'app-direct-entity-list',
    templateUrl: './direct-entity-list.component.html',
    styleUrls: ['./direct-entity-list.component.less'],
    standalone: false
})
export class DirectEntityListComponent implements OnInit,AfterViewInit, OnDestroy {

  constructor(private formBuilder:FormBuilder,private utilityService:UtilityService,
              private router:Router,private activatedRoute:ActivatedRoute,private permissionService:PermissionService,
              private service:DataService,private logger:NGXLogger) { }

  directEntityColumns=['code','name','cityName','countryName','action'];
  pageSizeOptions=this.utilityService.getPageSizeOptions();
  totalRecords:any;
  viewPermission:boolean=false;
  addPermission:boolean=false;
  editPermission:boolean=false;
  directEntityListForm:FormGroup;
  directEntityDataSource=new MatTableDataSource<IDirectEntity>();
  criteria:IDirectEntityCriteria={};
  @ViewChild(MatPaginator) paginator:MatPaginator;
  @ViewChild(MatSort) sort:MatSort;
	subscription = new Subscription();

  ngOnInit(): void {
    this.checkPermissions();
    this.initializeForm();
    this.setCodeValueChangeEvent();
    this.setNameValueChangeEvent();
    this.setCityValueChangeEvent();
    this.setCountryValueChangeEvent();
  }

  private initializeForm(){
    this.directEntityListForm=this.formBuilder.group({
       id:[],
       code:[],
       name:[],
       cityName:[],
       countryName:[],
    })
  }

  ngAfterViewInit(): void {
    this.directEntityDataSource.paginator = this.paginator;
    this.directEntityDataSource.sort = this.sort;
    this.paginator.page.pipe(tap(() => this.getDirectEntityList())).subscribe();
    this.getDirectEntityList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  sortData(event:any){
     this.criteria.sort=[event.active,event.direction];
     this.getDirectEntityList(true);
  }

  private setCodeValueChangeEvent(){
    this.subscription = this.code.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(codeValue => {
       this.criteria['code.contains']=codeValue?.toLowerCase();
       this.getDirectEntityList(true);
    });
  }

  private setNameValueChangeEvent(){
    const sub = this.name.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(nameValue => {
       this.criteria['name.contains']=nameValue?.toLowerCase();
       this.getDirectEntityList(true);
    });
    this.subscription.add(sub);
  }

  private setCountryValueChangeEvent(){
    const sub = this.countryName.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(countryValue => {
       this.criteria.countryName=countryValue?.toLowerCase();
       this.getDirectEntityList(true);
    });
    this.subscription.add(sub);
  }

  private setCityValueChangeEvent(){
    const sub = this.cityName.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(cityValue => {
       this.criteria.cityName=cityValue?.toLowerCase();
       this.getDirectEntityList(true);
    });
    this.subscription.add(sub);
  }

  openEntityForm(id?:number){
    if(id){
      this.editPermission ? this.router.navigate(['edit/'+id],{relativeTo:this.activatedRoute}) :
        this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING,"warning");
    } else {
      this.addPermission ? this.router.navigate(['add'],{relativeTo:this.activatedRoute}) :
        this.utilityService.notifier(Message.ADD_PERMISSION_WARNING,"warning");
    }
  }

  private getDirectEntityList(resetPageIndex: boolean =false){
    if (resetPageIndex) {
      this.paginator.pageIndex = 0;
    }
    if(this.viewPermission){
      this.criteria.page=this.paginator?.pageIndex;
      this.criteria.size=this.paginator?.pageSize;
      this.criteria.sort = this.criteria.sort?.length=== undefined ? ['createdDate','desc']:this.criteria.sort;
      this.service.get(Api.DIRECT_ENTITY,this.criteria).subscribe(res => {
        if(res){
          if(res.contents.length==0){
            this.directEntityDataSource=res?.contents;
            this.totalRecords=res?.totalRecords;
            this.utilityService.notifier('No Records Found','info');
          }
          this.logger.info('Direct Entity List',res);
          this.directEntityDataSource=res?.contents;
          this.totalRecords=res?.totalRecords;
          if (this.paginator.pageIndex >= Math.ceil(this.totalRecords / this.paginator.pageSize)) {
            this.paginator.pageIndex = 0;
          }
        }
      });
    } else
        this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING,"warning");
  }

  private checkPermissions(){
    const permissionCodes=[PermissionKey.DIRECT_ENTITY_MODULE_ADD_PERMISSION,PermissionKey.DIRECT_ENTITY_MODULE_VIEW_PERMISSION,
      PermissionKey.DIRECT_ENTITY_MODULE_EDIT_PERMISSION];
    const permissionsWithShowValue:IPermissionWithShow[]=this.permissionService.checkPermissions(permissionCodes,PermissionKey.DIRECT_ENTITY);
    this.addPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.DIRECT_ENTITY_MODULE_ADD_PERMISSION})?.show?true:false;
    this.viewPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.DIRECT_ENTITY_MODULE_VIEW_PERMISSION})?.show?true:false;
    this.editPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.DIRECT_ENTITY_MODULE_EDIT_PERMISSION})?.show?true:false;
  }

  get code(){ return this.directEntityListForm.get('code'); }
  get name(){ return this.directEntityListForm.get('name'); }
  get cityName(){ return this.directEntityListForm.get('cityName'); }
  get countryName(){ return this.directEntityListForm.get('countryName'); }
}
